import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Minus, Plus } from 'lucide-react';
import { US_STATE_PATHS } from './usStatePaths';

export const MAP_W = 1000;
export const MAP_H = 465;
export const MAP_LNG_MIN = -115;
export const MAP_LNG_MAX = -74;
export const MAP_LAT_MIN = 24;
export const MAP_LAT_MAX = 39.5;

const ZOOM_MIN = 1;
const ZOOM_MAX = 2.75;
const ZOOM_STEP = 0.4;

export type MapPoint = {
  id: string;
  name: string;
  city: string;
  abbr: string;
  href: string;
  x: number;
  y: number;
};

type SlotData = Record<string, string | null | undefined>;

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

export function StateButton({
  data,
  count,
  active,
  onSelect,
  onPreview,
}: {
  data: SlotData;
  count: number;
  active: boolean;
  onSelect: () => void;
  onPreview: (on: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      onMouseEnter={() => onPreview(true)}
      onMouseLeave={() => onPreview(false)}
      onFocus={() => onPreview(true)}
      onBlur={() => onPreview(false)}
      aria-pressed={active}
      className={`flex h-full w-full items-start justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-300 ${
        active
          ? 'border-[rgb(var(--ala-secondary))] bg-[rgb(var(--ala-secondary))]/15'
          : 'border-[rgb(var(--ala-on-primary))]/10 bg-[rgb(var(--ala-on-primary))]/[0.03] hover:border-[rgb(var(--ala-on-primary))]/30'
      }`}
    >
      <span className="min-w-0">
        <span className="block font-heading text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgb(var(--ala-on-primary))]/50">
          {data.region}
        </span>
        <span className="block font-heading text-base font-semibold text-[rgb(var(--ala-on-primary))]">
          {data.name}
        </span>
        <span className="mt-0.5 line-clamp-2 font-body text-xs text-[rgb(var(--ala-on-primary))]/60">
          {data.markets}
        </span>
      </span>
      <span
        className={`shrink-0 rounded-full px-2.5 py-1 font-heading text-xs font-semibold tabular-nums ${
          active
            ? 'bg-[rgb(var(--ala-secondary))] text-[rgb(var(--ala-on-secondary))]'
            : 'bg-[rgb(var(--ala-on-primary))]/10 text-[rgb(var(--ala-on-primary))]'
        }`}
      >
        {count}
      </span>
    </button>
  );
}

export function FootprintMap({ points, focus }: { points: MapPoint[]; focus: string | null }) {
  const [hovered, setHovered] = useState<MapPoint | null>(null);
  const [zoom, setZoom] = useState(ZOOM_MIN);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    active: boolean;
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startPanX: number;
    startPanY: number;
    moved: boolean;
  } | null>(null);
  const [dragging, setDragging] = useState(false);
  const focusRef = useRef(focus);
  focusRef.current = focus;

  const labels = useMemo(() => {
    const groups: Record<string, { x: number; y: number; n: number }> = {};
    for (const p of points) {
      const g = groups[p.abbr] || (groups[p.abbr] = { x: 0, y: 0, n: 0 });
      g.x += p.x;
      g.y += p.y;
      g.n += 1;
    }
    return Object.entries(groups).map(([abbr, g]) => ({ abbr, x: g.x / g.n, y: g.y / g.n }));
  }, [points]);

  const vbW = MAP_W / zoom;
  const vbH = MAP_H / zoom;
  const maxPanX = (MAP_W - vbW) / 2;
  const maxPanY = (MAP_H - vbH) / 2;
  const panX = clamp(pan.x, -maxPanX, maxPanX);
  const panY = clamp(pan.y, -maxPanY, maxPanY);
  const vbX = MAP_W / 2 + panX - vbW / 2;
  const vbY = MAP_H / 2 + panY - vbH / 2;

  const applyPanForZoom = useCallback((x: number, y: number, nextZoom: number) => {
    const nextVbW = MAP_W / nextZoom;
    const nextVbH = MAP_H / nextZoom;
    const nextMaxX = (MAP_W - nextVbW) / 2;
    const nextMaxY = (MAP_H - nextVbH) / 2;
    setPan({
      x: clamp(x - MAP_W / 2, -nextMaxX, nextMaxX),
      y: clamp(y - MAP_H / 2, -nextMaxY, nextMaxY),
    });
  }, []);

  useEffect(() => {
    if (!focus) return;
    const label = labels.find((l) => l.abbr === focus);
    if (!label) return;
    applyPanForZoom(label.x, label.y, zoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-center on focus change
  }, [focus, labels, applyPanForZoom]);

  const setZoomLevel = useCallback(
    (nextZoom: number) => {
      const z = clamp(Number(nextZoom.toFixed(2)), ZOOM_MIN, ZOOM_MAX);
      if (z <= ZOOM_MIN) {
        setZoom(ZOOM_MIN);
        setPan({ x: 0, y: 0 });
        return;
      }
      const focusAbbr = focusRef.current;
      const label = focusAbbr ? labels.find((l) => l.abbr === focusAbbr) : null;
      const originX = label ? label.x : MAP_W / 2 + panX;
      const originY = label ? label.y : MAP_H / 2 + panY;
      setZoom(z);
      applyPanForZoom(originX, originY, z);
    },
    [labels, panX, panY, applyPanForZoom],
  );

  const zoomIn = useCallback(() => setZoomLevel(zoom + ZOOM_STEP), [setZoomLevel, zoom]);
  const zoomOut = useCallback(() => setZoomLevel(zoom - ZOOM_STEP), [setZoomLevel, zoom]);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (zoom <= ZOOM_MIN || event.button !== 0) return;
    // Ignore pinches / multi-touch.
    if (event.isPrimary === false) return;
    dragRef.current = {
      active: true,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startPanX: panX,
      startPanY: panY,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag?.active || drag.pointerId !== event.pointerId) return;
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dx = ((event.clientX - drag.startClientX) / rect.width) * vbW;
    const dy = ((event.clientY - drag.startClientY) / rect.height) * vbH;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) drag.moved = true;
    setPan({
      x: clamp(drag.startPanX - dx, -maxPanX, maxPanX),
      y: clamp(drag.startPanY - dy, -maxPanY, maxPanY),
    });
  };

  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setDragging(false);
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* already released */
    }
  };

  const canZoomIn = zoom < ZOOM_MAX - 0.01;
  const canZoomOut = zoom > ZOOM_MIN + 0.01;

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        className={`relative h-[180px] overflow-hidden rounded-2xl touch-pan-y sm:h-[210px] lg:h-auto ${
          zoom > ZOOM_MIN ? (dragging ? 'cursor-grabbing' : 'cursor-grab') : ''
        }`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <svg
          viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
          className="h-full w-full touch-manipulation lg:h-auto"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="Map of American Landmark communities"
        >
          <defs>
            <pattern id="investors-map-grid" width="16" height="16" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.2" fill="rgba(255,255,255,0.07)" />
            </pattern>
            <radialGradient id="investors-map-glow">
              <stop offset="0%" className="[stop-color:rgb(var(--ala-secondary))]" stopOpacity="0.7" />
              <stop offset="100%" className="[stop-color:rgb(var(--ala-secondary))]" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect width={MAP_W} height={MAP_H} fill="url(#investors-map-grid)" />

          <g strokeLinejoin="round" aria-hidden>
            {US_STATE_PATHS.map((s) => {
              const active = focus === s.abbr;
              return (
                <path
                  key={s.abbr}
                  d={s.d}
                  className={
                    active
                      ? 'fill-[rgb(var(--ala-secondary))] stroke-[rgb(var(--ala-secondary))]'
                      : 'fill-white stroke-white'
                  }
                  fillOpacity={active ? 0.22 : 0.07}
                  strokeOpacity={active ? 0.9 : 0.35}
                  strokeWidth={active ? 2 / zoom : 1.25 / zoom}
                  style={{ transition: 'fill-opacity 0.3s, stroke-opacity 0.3s' }}
                />
              );
            })}
          </g>

          {labels.map((l) => {
            const on = !focus || focus === l.abbr;
            return (
              <g key={l.abbr}>
                {focus === l.abbr ? (
                  <circle
                    cx={l.x}
                    cy={l.y}
                    r={46 / Math.sqrt(zoom)}
                    fill="none"
                    strokeOpacity="0.5"
                    className="animate-ping stroke-[rgb(var(--ala-secondary))] motion-reduce:animate-none"
                    style={{
                      transformBox: 'fill-box',
                      transformOrigin: 'center',
                      animationDuration: '2.4s',
                    }}
                  />
                ) : null}
                <text
                  x={l.x}
                  y={l.y - 34 / Math.sqrt(zoom)}
                  textAnchor="middle"
                  className="font-heading"
                  fontSize={15 / Math.sqrt(zoom)}
                  fontWeight="600"
                  letterSpacing="2"
                  fill="#ffffff"
                  style={{ opacity: on ? 0.85 : 0.2, transition: 'opacity 0.3s' }}
                >
                  {l.abbr}
                </text>
              </g>
            );
          })}

          {points.map((p) => {
            const on = !focus || focus === p.abbr;
            const baseR = on ? 6.5 : 4;
            const hoverR = 10;
            const glowR = on ? 22 : 0;
            const scale = 1 / Math.sqrt(zoom);
            return (
              <a
                key={p.id}
                href={p.href}
                aria-label={`${p.name}, ${p.city}`}
                onClick={(e) => {
                  if (dragRef.current?.moved) e.preventDefault();
                }}
                onMouseEnter={() => setHovered(p)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered(p)}
                onBlur={() => setHovered(null)}
              >
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={glowR * scale}
                  fill="url(#investors-map-glow)"
                  style={{ transition: 'r 0.4s' }}
                />
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={(hovered?.id === p.id ? hoverR : baseR) * scale}
                  className={on ? 'fill-[rgb(var(--ala-secondary))]' : 'fill-white'}
                  fillOpacity={on ? 1 : 0.2}
                  stroke={on ? '#ffffff' : 'none'}
                  strokeOpacity={hovered?.id === p.id ? 1 : 0.85}
                  strokeWidth={(hovered?.id === p.id ? 2.5 : 1.5) * scale}
                  style={{ transition: 'r 0.25s, fill-opacity 0.3s' }}
                />
              </a>
            );
          })}
        </svg>

        <AnimatePresence>
          {hovered ? (
            <motion.div
              key={hovered.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-[calc(100%+14px)] whitespace-nowrap rounded-lg bg-[rgb(var(--ala-background))] px-3 py-2 shadow-xl"
              style={{
                left: `${((hovered.x - vbX) / vbW) * 100}%`,
                top: `${((hovered.y - vbY) / vbH) * 100}%`,
              }}
            >
              <span className="block font-heading text-sm font-semibold text-[rgb(var(--ala-text))]">
                {hovered.name}
              </span>
              <span className="block font-body text-xs text-[rgb(var(--ala-text-muted))]">
                {hovered.city}, {hovered.abbr}
              </span>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {/* Desktop corner controls */}
        <div
          className="absolute bottom-3 right-3 z-30 hidden overflow-hidden rounded-xl border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] shadow-lg lg:flex lg:flex-col"
          role="group"
          aria-label="Map zoom"
        >
          <button
            type="button"
            onClick={zoomIn}
            disabled={!canZoomIn}
            aria-label="Zoom in"
            className="flex h-10 w-10 items-center justify-center text-[rgb(var(--ala-primary))] transition hover:bg-[rgb(var(--ala-secondary))]/15 disabled:opacity-35"
          >
            <Plus className="h-4 w-4" strokeWidth={2.5} />
          </button>
          <div className="h-px bg-[rgb(var(--ala-border))]" aria-hidden />
          <button
            type="button"
            onClick={zoomOut}
            disabled={!canZoomOut}
            aria-label="Zoom out"
            className="flex h-10 w-10 items-center justify-center text-[rgb(var(--ala-primary))] transition hover:bg-[rgb(var(--ala-secondary))]/15 disabled:opacity-35"
          >
            <Minus className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Mobile / tablet zoom bar — in normal document flow so it can't be clipped or missed */}
      <div
        className="mt-3 grid grid-cols-2 gap-2 lg:hidden"
        role="group"
        aria-label="Map zoom"
      >
        <button
          type="button"
          onClick={zoomIn}
          disabled={!canZoomIn}
          aria-label="Zoom in"
          className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-[rgb(var(--ala-secondary))] px-4 font-heading text-sm font-semibold text-[rgb(var(--ala-on-secondary))] transition active:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Plus className="h-4 w-4" strokeWidth={2.5} />
          Zoom in
        </button>
        <button
          type="button"
          onClick={zoomOut}
          disabled={!canZoomOut}
          aria-label="Zoom out"
          className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-[rgb(var(--ala-on-primary))]/25 bg-[rgb(var(--ala-on-primary))]/10 px-4 font-heading text-sm font-semibold text-[rgb(var(--ala-on-primary))] transition active:bg-[rgb(var(--ala-on-primary))]/20 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Minus className="h-4 w-4" strokeWidth={2.5} />
          Zoom out
        </button>
      </div>
    </div>
  );
}
